/**
 * Toast — Sonner-backed thin wrapper.
 *
 * Choice (orchestrator brief task 5.3, research-B §9.2 / PLAN 5.7): Sonner
 * over the shadcn radix Toast primitive. Reasons:
 *   - smaller bundle (~6 KB vs Radix Toast + custom queueing logic)
 *   - already provides queueing, swipe-to-dismiss, action buttons,
 *     promise-aware variants out of the box
 *   - design-analysis recommendation; matches research
 *
 * To callers we expose the same `toast(...)` import surface so we can swap
 * the impl later without touching call-sites.
 */
import { toast as sonnerToast, type ExternalToast } from 'sonner';

export type ToastVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

export interface ToastOptions extends Omit<ExternalToast, 'id'> {
  id?: string | number;
  variant?: ToastVariant;
}

/**
 * Show a toast. `variant` maps to a Sonner type so warm-theme styling can
 * stay in one place (the <Toaster /> in toaster.tsx). Returns the toast id
 * so callers can dismiss programmatically.
 */
export function toast(message: string, opts: ToastOptions = {}): string | number {
  const { variant = 'default', ...rest } = opts;
  switch (variant) {
    case 'success':
      return sonnerToast.success(message, rest);
    case 'warning':
      return sonnerToast.warning(message, rest);
    case 'error':
      return sonnerToast.error(message, rest);
    case 'info':
      return sonnerToast.info(message, rest);
    default:
      return sonnerToast(message, rest);
  }
}

toast.dismiss = sonnerToast.dismiss.bind(sonnerToast);
toast.promise = sonnerToast.promise.bind(sonnerToast);

export { sonnerToast };
